import React, { useState } from 'react';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const ImportModal = ({ show, onClose, onHide, onImportSuccess, onSuccess }) => {
  const handleClose = onClose || onHide || (() => {});
  const handleSuccess = onImportSuccess || onSuccess || (() => {});

  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!show) return null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async (runAsDry) => {
    if (!file) {
      showToast.warning('Please select a CSV file first.');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await streetlearnApi.importQuestions(formData, runAsDry);
      setResult(res);

      if (!runAsDry && res.imported > 0) {
        showToast.success(`Successfully imported ${res.imported} questions!`);
        handleSuccess();
      } else if (runAsDry) {
        showToast.info('Dry run completed. Check preview below.');
      }
    } catch (err) {
      console.error('Import error:', err);
      showToast.error(err?.response?.data?.message || 'Failed to process CSV file.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title font-bold m-0">Import Questions via CSV</h5>
            <button
              type="button"
              className="close"
              style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
              onClick={handleClose}
            >
              <span>&times;</span>
            </button>
          </div>

          <div className="modal-body">

            <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
              <strong>CSV Columns Required:</strong>
              <br />
              <code>level_id, lesson_code, stem, option_a, option_b, option_c, option_d, correct_option, explanation, use_in, status</code>
              <br />
              <small className="text-muted">
                <code>correct_option</code> should be A, B, C, or D. <code>use_in</code> can be 'quiz', 'exam', or 'both'.
              </small>
            </div>

            <div className="form-group mb-3">
              <label className="font-semibold text-sm">Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                className="form-control"
                onChange={handleFileChange}
              />
            </div>

            {/* Dry Run vs Real Import Buttons */}
            <div className="d-flex gap-2 mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleUpload(true)}
                disabled={!file || loading}
              >
                {loading && dryRun ? 'Validating...' : '🔍 Validate & Dry Run'}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleUpload(false)}
                disabled={!file || loading || (result && result.errors && result.errors.length > 0)}
              >
                {loading && !dryRun ? 'Importing...' : '📥 Commit Import to Database'}
              </button>
            </div>

            {/* Results Preview */}
            {result && (
              <div className="mt-3 p-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="font-bold">Validation Summary:</span>
                  <span
                    className={`badge ${
                      result.errors && result.errors.length > 0 ? 'badge-danger' : 'badge-success'
                    }`}
                  >
                    {result.errors && result.errors.length > 0 ? 'Errors Detected' : 'All Valid'}
                  </span>
                </div>

                <div className="row text-center mb-2">
                  <div className="col-3">
                    <small className="text-muted">Total Rows</small>
                    <div className="font-bold">{result.rows}</div>
                  </div>
                  <div className="col-3">
                    <small className="text-muted">Valid Rows</small>
                    <div className="font-bold text-success">{result.valid}</div>
                  </div>
                  <div className="col-3">
                    <small className="text-muted">Errors</small>
                    <div className="font-bold text-danger">{result.errors?.length || 0}</div>
                  </div>
                  <div className="col-3">
                    <small className="text-muted">Imported</small>
                    <div className="font-bold text-primary">{result.imported || 0}</div>
                  </div>
                </div>

                {/* Error List */}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2">
                    <span className="text-danger font-semibold text-sm">Row Validation Errors:</span>
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }} className="mt-1">
                      <table className="table table-sm table-bordered text-xs mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Row #</th>
                            <th>Error Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.errors.map((err, i) => (
                            <tr key={i}>
                              <td>{err.row}</td>
                              <td className="text-danger">{err.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-default btn-sm" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
